/**
 * The 말랑이's sounds, synthesised with Web Audio (no files). It is a rice cake (인절미) gone memory foam, so
 * everything is dull, low and sticky — no squeaks, no bright clicks:
 *   press    "푹"   a soft, heavy thump with a short muffled squish as the finger sinks into the cake
 *   sink     "쯔윽" small sticky give while the finger keeps sinking in (low, more the faster it goes in)
 *   rub      "찌걱찌걱" while it is pulled or kneaded: a low sticky drag, broken by little tacky grains (the cake
 *            parting from itself), louder and denser the faster the finger moves
 *   release  "쩍"   the sticky cake letting go of the finger, then a soft low settle
 * Everything is low-passed: nothing hisses. But each sound keeps its body around 300 Hz - 1 kHz: a phone speaker
 * plays next to nothing below ~350 Hz, so a purely low thump is silent on the phone (the low tones are for headphones).
 * The context is built at idle and only resumed on a touch (iOS).
 */
const LEAD = 0.004;
const db = (d: number) => Math.pow(10, d / 20);
const U = (a: number, b: number) => a + (b - a) * Math.random();

type Rub = { src: AudioBufferSourceNode; osc: OscillatorNode; g: GainNode; bp: BiquadFilterNode; grains: number };

export class MallangAudio {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private rubs = new Map<number, Rub>();
  private sinkAcc = 0;
  private lastSink = -1;
  private lastPress = -1;
  onState: (running: boolean) => void = () => {};
  wanted = false;

  get running() { return !!this.ctx && this.ctx.state === "running"; }
  private now() { return this.ctx!.currentTime; }

  /** Create/resume on a user gesture. Safe to call often. */
  unlock() {
    try {
      if (!this.ctx || this.ctx.state === "closed") this.build();
      const ctx = this.ctx!;
      const done = () => {
        if (ctx.state !== "running") { this.onState(false); return; }
        const b = ctx.createBuffer(1, 1, ctx.sampleRate);
        const s = ctx.createBufferSource(); s.buffer = b; s.connect(ctx.destination); s.start();
        const nav = navigator as Navigator & { audioSession?: { type: string } };
        if (nav.audioSession && nav.audioSession.type !== "playback") nav.audioSession.type = "playback";
        this.onState(true);
      };
      if (ctx.state !== "running") ctx.resume().then(done, () => this.onState(false));
      else done();
    } catch {
      this.onState(false);
    }
  }

  /** Builds the (suspended) context at idle: creating it inside the first touch would freeze that touch. */
  prewarm() { try { if (!this.ctx || this.ctx.state === "closed") this.build(); } catch { /* no Web Audio */ } }

  resumeExisting() {
    const ctx = this.ctx;
    if (!ctx || ctx.state === "closed" || ctx.state === "running") return;
    ctx.resume().then(() => this.onState(ctx.state === "running"), () => {});
  }

  private build() {
    const ctx = new AudioContext();
    const master = ctx.createGain(); master.gain.value = 1;
    const safe = ctx.createWaveShaper(), c = new Float32Array(1025);
    for (let i = 0; i < c.length; i++) { const x = i / 512 - 1, a = Math.abs(x); c[i] = a <= 0.9 ? x : Math.sign(x) * (0.9 + 0.09 * Math.tanh((a - 0.9) / 0.09)); }
    safe.curve = c;
    master.connect(safe); safe.connect(ctx.destination);
    const noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 2), ctx.sampleRate);
    const d = noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.ctx = ctx; this.master = master; this.noise = noise;
    this.rubs.clear();
  }

  dispose() {
    for (const k of [...this.rubs.keys()]) this.rubEnd(k, 0);
    try { this.ctx?.close(); } catch { /* */ }
    this.ctx = null;
  }

  // ---------- building blocks ----------

  /** A sine that glides f0 → f1, with a quick attack and an exponential tail. */
  private tone(t: number, f0: number, f1: number, dur: number, peak: number, att = 0.006) {
    const ctx = this.ctx!, o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(peak, t + att); g.gain.setTargetAtTime(0, t + att, dur / 3);
    o.connect(g); g.connect(this.master!);
    o.start(t); o.stop(t + att + dur * 2.2);
  }

  /** Filtered noise: a lowpass (cutoff sweeping f0 → f1) into a bandpass, with an attack and a tail. */
  private squishNoise(t: number, f0: number, f1: number, bpF: number, q: number, dur: number, peak: number) {
    const ctx = this.ctx!, s = ctx.createBufferSource(); s.buffer = this.noise;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass";
    lp.frequency.setValueAtTime(f0, t); lp.frequency.exponentialRampToValueAtTime(Math.max(40, f1), t + dur);
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = bpF; bp.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(peak, t + 0.012); g.gain.setTargetAtTime(0, t + 0.012, dur / 3);
    s.connect(lp); lp.connect(bp); bp.connect(g); g.connect(this.master!);
    s.start(t, Math.random() * 1.5); s.stop(t + dur * 2.4);
  }

  // ---------- events ----------

  /** A finger lands: "푹". `m` scales the pitch (a bigger toy sounds lower). */
  press(m = 1) {
    if (!this.running) return;
    const t = this.now() + LEAD;
    if (t - this.lastPress < 0.05) return;
    this.lastPress = t;
    // the body of the thump sits where a phone speaker can play it (~250-600 Hz); a low tone under it for headphones
    this.tone(t, 300 * m * U(0.92, 1.08), 170 * m, 0.1, db(-14), 0.01);
    this.tone(t, 130 * m * U(0.92, 1.08), 80 * m, 0.13, db(-17), 0.012);
    this.squishNoise(t, 1300, 320, 520 * m, 0.9, 0.15, db(-3));
  }

  /** The finger keeps sinking in by `amount` (fraction of the radius this frame): small sticky gives, "쯔윽". */
  sink(amount: number, depth: number, m = 1) {
    if (!this.running || !(amount > 0 && amount < 1)) return;
    this.sinkAcc = Math.min(0.05, this.sinkAcc + amount);
    const step = 0.016, t = this.now() + LEAD;
    if (this.sinkAcc < step || t - this.lastSink < 0.05) return;
    this.sinkAcc -= step; this.lastSink = t;
    const f = (820 - 260 * Math.min(1, depth / 0.3)) * m * U(0.85, 1.15);
    this.squishNoise(t, f * 1.8, f * 0.6, f, 2.4, 0.045, db(U(-15, -10)));
  }

  /**
   * A finger pulling or kneading it, `speed` px/s: "찌걱찌걱" — a low sticky drag whose loudness follows the speed,
   * broken by little tacky grains (the cake parting from itself), denser the faster it goes.
   */
  rub(id: number, speed: number, depth: number) {
    if (!this.running) return;
    let r = this.rubs.get(id);
    if (!r) {
      const ctx = this.ctx!, t = this.now();
      const src = ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1000;
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 450; bp.Q.value = 0.9;
      const osc = ctx.createOscillator(); osc.type = "sine"; osc.frequency.value = 70;
      const og = ctx.createGain(); og.gain.value = 0.04;
      const g = ctx.createGain(); g.gain.setValueAtTime(0, t);
      src.connect(lp); lp.connect(bp); bp.connect(g); osc.connect(og); og.connect(g); g.connect(this.master!);
      src.start(t, Math.random()); osc.start(t);
      r = { src, osc, g, bp, grains: 0 };
      this.rubs.set(id, r);
    }
    const t = this.now(), v = Math.min(1, speed / 900);
    const lvl = speed > 25 ? db(-21 + 7 * v) * (0.7 + 0.3 * Math.min(1, depth / 0.15)) : 0;
    r.g.gain.cancelScheduledValues(t); r.g.gain.setTargetAtTime(lvl, t, 0.04);
    r.bp.frequency.setTargetAtTime(380 + 220 * v, t, 0.06);
    r.osc.frequency.setTargetAtTime(64 + 18 * v, t, 0.06);
    // the tacky grains: up to ~25 a second at a fast pull (rub() runs once per 1/60 s step)
    if (speed > 40) {
      r.grains += (speed / 900) * 0.3;
      if (r.grains >= 1) {
        r.grains -= 1;
        const f = U(520, 1150);
        this.squishNoise(t + LEAD + U(0, 0.012), f * 2, f * 0.7, f, 2.6, U(0.016, 0.03), db(U(-19, -13) + 4 * v));
      }
    }
  }

  rubEnd(id: number, fadeMs = 70) {
    const r = this.rubs.get(id); if (!r || !this.ctx) return;
    const t = this.now(), end = t + fadeMs / 1000;
    r.g.gain.cancelScheduledValues(t); r.g.gain.setTargetAtTime(0, t, Math.max(0.005, fadeMs / 3000));
    try { r.src.stop(end + 0.05); r.osc.stop(end + 0.05); } catch { /* */ }
    this.rubs.delete(id);
  }

  /** The sticky cake lets go of the finger, `depth` how much it held: "쩍", then a soft low settle. */
  release(depth: number, m = 1) {
    if (!this.running) return;
    const t = this.now() + LEAD, k = Math.min(1, depth / 0.25);
    if (k < 0.08) return;
    // the tacky snap: a short, low, muffled burst (no rising pitch: it parts, it does not pop)
    this.squishNoise(t, 2400, 600, 900 * m, 1.4, 0.05, db(-6 + 6 * k));
    this.tone(t, 280 * m * U(0.95, 1.05), 150 * m, 0.07, db(-19 + 6 * k), 0.003);
    // the heavy cake settling back
    this.tone(t + 0.07, 190 * m * U(0.95, 1.05), 120 * m, 0.22, db(-26 + 5 * k), 0.04);
    this.squishNoise(t + 0.07, 900, 300, 420 * m, 1, 0.18, db(-14 + 4 * k));
  }
}
